/**
 * The message a person already typed, held across the one reload a dead
 * session still requires.
 *
 * The access token lives in an HttpOnly cookie. When that cookie does not
 * arrive, the only recovery that re-runs auth bootstrap is a page load. The
 * in-memory chat store does not survive it, so without this the reload
 * throws away the send and the banner tells them to type it again.
 *
 * Parked only for a send the server never accepted. A refusal on a later
 * reconnect means the turn already exists; replaying it would send the
 * message twice, and the reload reattaches to that turn anyway.
 */

import type { UploadedImage } from './types';

export interface ParkedChatSend {
  /** The conversation it was sent to, when the store had one. */
  conversationId: string | null;
  /**
   * The message was starting its conversation. A new chat's id is minted in
   * the browser and never reached the server, so after the reload that chat
   * 404s and resets to an empty one — the id no longer names anything, and an
   * empty chat is where the message belongs.
   */
  startsConversation: boolean;
  message: string;
  displayText?: string;
  isHidden?: boolean;
  /** Already uploaded, with the File dropped — the same shape Retry re-sends. */
  images: UploadedImage[];
  /** The typed message's identity, so the replay counts as the same send. */
  sendId?: string;
  /**
   * Whether it may send itself. False when an earlier POST of it may have
   * reached the server (a 502 before acknowledgement): replaying could send it
   * twice, so its text goes back in the composer for the person to decide.
   */
  autoSend: boolean;
  /** Tenant pins at park time. A replay into any other tenant is dropped. */
  workspaceId?: string;
  masqueradeId?: string;
  /**
   * The person who typed it. The home pin is the literal `home` for everyone,
   * so the pins alone cannot tell the next person on this tab from this one.
   */
  userId?: string;
  parkedAtMs: number;
}

const STORAGE_KEY = 'leo.parkedChatSend';
/** A parked send from a previous sitting must not fire on the next visit. */
export const PARKED_SEND_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * An image survives the reload only once the server has it. A picked file
 * that never finished uploading exists only in the page being thrown away,
 * and replaying the caption without it would send a different message.
 */
export function imagesSurviveReload(images: UploadedImage[] | undefined): boolean {
  return (images ?? []).every((image) => Boolean(image.s3_url || image.asset_id));
}

/**
 * Nothing has answered yet and this is the only thing the person has said:
 * the send was starting its conversation.
 */
export function startsConversation(messages: ReadonlyArray<{ type: string }>): boolean {
  return !messages.some((m) => m.type === 'assistant')
    && messages.filter((m) => m.type === 'user').length <= 1;
}

export function parkChatSend(
  send: Omit<ParkedChatSend, 'parkedAtMs'>,
  storage: Storage,
  nowMs: number,
): void {
  const parked: ParkedChatSend = {
    ...send,
    images: send.images.map((image) => ({ ...image, file: null })),
    parkedAtMs: nowMs,
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(parked));
  } catch { /* A full or blocked storage loses the park, not the page. */ }
}

export function readParkedChatSend(storage: Storage, nowMs: number): ParkedChatSend | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: ParkedChatSend;
  try {
    parsed = JSON.parse(raw) as ParkedChatSend;
  } catch {
    clearParkedChatSend(storage);
    return null;
  }
  if (
    (parsed?.conversationId !== null && typeof parsed?.conversationId !== 'string')
    || typeof parsed.message !== 'string'
    || typeof parsed.startsConversation !== 'boolean'
    || typeof parsed.autoSend !== 'boolean'
    || typeof parsed.parkedAtMs !== 'number'
    || !Array.isArray(parsed.images)
    || nowMs - parsed.parkedAtMs > PARKED_SEND_MAX_AGE_MS
  ) {
    clearParkedChatSend(storage);
    return null;
  }
  return parsed;
}

export function takeParkedChatSend(storage: Storage, nowMs: number): ParkedChatSend | null {
  const parked = readParkedChatSend(storage, nowMs);
  if (parked) clearParkedChatSend(storage);
  return parked;
}

/** Forget the parked message — on sign-out, the tab may be the next person's. */
export function clearParkedChatSend(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch { /* nothing to recover */ }
}

export type ParkedSendTarget = 'send' | 'restore' | 'wait' | 'drop';

/**
 * Where a parked message goes once the page is back.
 *
 * `send` when the same tenant is on the same conversation, or on an empty
 * chat for a message that was starting one. `restore` puts the text back in the
 * composer when the message cannot be sent as it was typed, or may already
 * have reached the server. `wait` while the
 * chat is busy or still loading. `drop` for another tenant or another person —
 * their text must not surface there, sent or not — and for a park or a page
 * that cannot say whose it is.
 */
export function parkedSendTarget(
  parked: ParkedChatSend,
  here: {
    conversationId: string | null | undefined;
    hasMessages: boolean;
    idle: boolean;
    workspaceId?: string;
    masqueradeId?: string;
    userId?: string;
  },
): ParkedSendTarget {
  if (!parked.userId || parked.userId !== here.userId) return 'drop';
  if ((parked.workspaceId ?? '') !== (here.workspaceId ?? '')) return 'drop';
  if ((parked.masqueradeId ?? '') !== (here.masqueradeId ?? '')) return 'drop';
  if (!here.idle) return 'wait';
  const sameChat = parked.conversationId !== null && parked.conversationId === here.conversationId;
  const itsFreshChat = parked.startsConversation && !here.hasMessages;
  if (!sameChat && !itsFreshChat) return 'wait';
  return parked.autoSend && imagesSurviveReload(parked.images) ? 'send' : 'restore';
}

const CLAIMS_KEY = 'leo.parkedSendClaims';

/**
 * Claim the one replay a parked message gets, across every tab.
 *
 * Duplicating a tab copies its sessionStorage, park included, so each copy
 * would send the message once. The claim lives in localStorage, which the
 * copies share: the first tab to claim a send id sends it, and the rest drop
 * their copy. Without a send id there is nothing to share, so it is allowed.
 */
export function claimParkedSend(storage: Storage, sendId: string | undefined, nowMs: number): boolean {
  if (!sendId) return true;
  let claims: Record<string, number> = {};
  try {
    const parsed: unknown = JSON.parse(storage.getItem(CLAIMS_KEY) || '{}');
    if (parsed && typeof parsed === 'object') claims = parsed as Record<string, number>;
  } catch { /* an unreadable ledger is an empty one */ }
  const live = Object.fromEntries(
    Object.entries(claims).filter(([, at]) => typeof at === 'number' && nowMs - at <= PARKED_SEND_MAX_AGE_MS),
  );
  if (live[sendId] !== undefined) return false;
  live[sendId] = nowMs;
  try {
    storage.setItem(CLAIMS_KEY, JSON.stringify(live));
  } catch {
    /* A storage that will not hold the claim cannot share it either; send. */
  }
  return true;
}
